import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { type ApiInvestmentResponse, parseDecimalInput } from './types';
import { INVESTMENTS_QUERY_KEY } from './useInvestments';

/** Formulário de edição da ficha (campos editáveis — DEC-040 / ADR-0011). */
export interface UpdateInvestmentForm {
  codename: string;
  fundId: number | null;
  leadUserId: string;
  primaryThesisId: number | null;
  /** MOIC esperado digitado (ex.: "2,1"); vazio = null. */
  moicExpected: string;
  /** TIR esperada digitada em % (ex.: "22"); vazio = null. */
  irrExpected: string;
  /** Split do cheque digitado (ex.: "180000000"); vazio = null. */
  checkDiscretionary: string;
  checkCoinvestment: string;
  teamUserIds: string[];
  tags: string[];
}

export interface UpdateInvestmentBody {
  codename: string;
  fundId: number;
  leadUserId: string;
  primaryThesisId: number | null;
  moicExpected: number | null;
  irrExpected: number | null;
  checkDiscretionaryBRL: number | null;
  checkCoinvestmentBRL: number | null;
  teamUserIds: string[];
  tags: string[];
}

/**
 * Monta o corpo do PUT /api/investments/{id} (função pura, testável). MOIC vai como múltiplo;
 * TIR é digitada em % e convertida para fração (22 → 0.22, como o backend espera).
 */
export function buildUpdateBody(form: UpdateInvestmentForm): UpdateInvestmentBody {
  const irrPct = parseDecimalInput(form.irrExpected);
  return {
    codename: form.codename.trim(),
    fundId: form.fundId ?? 0,
    leadUserId: form.leadUserId,
    primaryThesisId: form.primaryThesisId,
    moicExpected: parseDecimalInput(form.moicExpected),
    irrExpected: irrPct == null ? null : irrPct / 100,
    checkDiscretionaryBRL: parseDecimalInput(form.checkDiscretionary),
    checkCoinvestmentBRL: parseDecimalInput(form.checkCoinvestment),
    teamUserIds: form.teamUserIds,
    tags: form.tags,
  };
}

/**
 * Edita a ficha do Investimento: PUT /api/investments/{id} com If-Match (ETag da ficha). Em sucesso,
 * invalida a ficha e a lista do Portfólio. 412 → a ficha mudou → aviso + refetch.
 */
export function useUpdateInvestment() {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, etag, body }: { id: string; etag: string; body: UpdateInvestmentBody }) =>
      apiFetch<ApiInvestmentResponse>(`/api/investments/${id}`, {
        method: 'PUT',
        headers: { 'If-Match': `"${etag}"` },
        body,
      }),
    onSuccess: (resp) => {
      void qc.invalidateQueries({ queryKey: ['investment', resp.id] });
      void qc.invalidateQueries({ queryKey: INVESTMENTS_QUERY_KEY });
      toast(`Ficha atualizada: ${resp.codename}.`);
    },
    onError: (err, vars) => {
      if ((err as ApiError).status === 412) {
        toast('Esse investimento foi alterado por outra pessoa. Atualizei a ficha.', 'warn');
        void qc.invalidateQueries({ queryKey: ['investment', vars.id] });
      } else {
        toast('Não foi possível salvar a ficha. Confira os campos e tente de novo.', 'danger');
      }
    },
  });
}
